import { useState, useRef, useCallback, useEffect } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow
} from '@/components/ui/table';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger
} from '@/components/ui/dropdown-menu';
import {
  ArrowUpDown,
  ArrowUp,
  ArrowDown,
  MoreVertical,
  Eye,
  Loader2,
  Check,
  Clock,
  Lock,
  XCircle,
  CheckCircle2,
  FileText,
  Upload,
  Copy,
  MessageSquare
} from 'lucide-react';
import { ProspectLoan, ProspectSortConfig } from './types';
import { PostClosingProgress } from './PostClosingProgress';
import { format, differenceInDays } from 'date-fns';
import { cn } from '@/lib/utils';
import { toast } from 'sonner';
import { supabase } from '@/utils/supabase';
import { useAuth } from '@/contexts/useAuth';
import { useFeatureFlag } from '@/hooks/useFeatureFlag';
import { WarehouseLineSelect } from './WarehouseLineSelect';
import { useWarehouseLine } from '@/hooks/useWarehouseLine';
import { usePrimisCollateral } from '@/hooks/usePrimisCollateral';
import { matchWarehouseLine } from '../../../../supabase/functions/_shared/warehouse-line-contract';
import { PrimisCollateralDate } from './PrimisCollateralDate';
import { isExtendedUser } from '@/types/auth';
import { parseAsLocalDate } from '@/utils/dateHelpers';
import { createComponentLogger } from '@/utils/logger';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Separator } from '@/components/ui/separator';
import { getDocumentDownloadUrl } from '@/services/loanDocumentService';
import {
  getPostClosingComments,
  type PostClosingComment
} from '@/services/postClosingWorkflowService';

const log = createComponentLogger('PostClosingTable', 'encompass-prospects');

// ── Types ──────────────────────────────────────────────────────────────────────

interface PostClosingTableProps {
  prospects: ProspectLoan[];
  isLoading: boolean;
  isLoadingMore?: boolean;
  hasMore?: boolean;
  totalCount?: number;
  sortConfig: ProspectSortConfig;
  onSort: (field: string) => void;
  onViewDetails: (prospect: ProspectLoan) => void;
  onLoadMore?: () => void;
  onLoanUpdate?: (loanId: string, updates: Partial<ProspectLoan>) => void;
  /** MERS Transfer Date Audit variant: drop Progress, add Purchased, sortable MERS. */
  mersAuditMode?: boolean;
}

type DateSaveState = 'idle' | 'saving' | 'synced' | 'pending' | 'error';
type PostClosingTask = 'pccd' | 'mers';
const postClosingCommentsCache = new Map<string, { count: number; comments: PostClosingComment[] }>();

function isSavingState(state: DateSaveState): boolean {
  return state === 'saving';
}

function postClosingCommentsCacheKey(loan: ProspectLoan): string {
  return `${loan.organization_id || 'unknown'}:${loan.encompass_loan_id}`;
}

function toDateInputValue(dateString: string | null | undefined): string {
  if (!dateString) {
return '';
}
  const datePart = dateString.slice(0, 10);
  return /^\d{4}-\d{2}-\d{2}$/.test(datePart) ? datePart : '';
}

function dateInputValueToTimestamp(value: string): string | null {
  return value ? `${value}T12:00:00.000Z` : null;
}

interface PostClosingRowProps {
  warehouseEnabled?: boolean;
  loan: ProspectLoan;
  onViewDetails: (prospect: ProspectLoan) => void;
  onLoanUpdate?: (loanId: string, updates: Partial<ProspectLoan>) => void;
  formatCurrency: (amount: number) => string;
  formatDate: (dateString: string | null | undefined) => string;
  mersAuditMode?: boolean;
}

function formatAbsoluteDateTime(dateString: string | null | undefined): string {
  if (!dateString) {
return '-';
}
  try {
    return new Date(dateString).toLocaleString('en-US', {
      month: '2-digit',
      day: '2-digit',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    });
  } catch {
    return dateString;
  }
}

function compactPreview(content: string | null | undefined): string {
  return (content || '').replace(/\s+/g, ' ').trim();
}

function PostClosingCommentsCell({ loan }: { loan: ProspectLoan }) {
  const count = loan.post_closing_comments_count || 0;
  const cacheKey = postClosingCommentsCacheKey(loan);
  const [comments, setComments] = useState<PostClosingComment[] | null>(null);
  const [loading, setLoading] = useState(false);
  const [loaded, setLoaded] = useState(false);

  const loadComments = useCallback(async () => {
    if (!loan.encompass_loan_id || loading) {
return;
}

    const cached = postClosingCommentsCache.get(cacheKey);
    if (cached && cached.count === count) {
      setComments(cached.comments);
      setLoaded(true);
      return;
    }

    try {
      setLoading(true);
      const data = await getPostClosingComments(loan.encompass_loan_id);
      postClosingCommentsCache.set(cacheKey, {
        count: data.length,
        comments: data
      });
      setComments(data);
      setLoaded(true);
    } catch (err) {
      log.error('Failed to load post-closing comments', { loanId: loan.id }, err as Error);
      setComments([]);
      setLoaded(true);
    } finally {
      setLoading(false);
    }
  }, [cacheKey, count, loading, loan.encompass_loan_id, loan.id]);

  const latestPreview = compactPreview(loan.post_closing_latest_comment_preview);

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <button
          type="button"
          data-row-interactive="true"
          aria-label={count > 0 ? `${count} Post Closing comments` : 'No Post Closing comments'}
          onMouseEnter={loadComments}
          onFocus={loadComments}
          onClick={(e) => e.stopPropagation()}
          className={cn(
            'flex w-full min-w-0 items-center gap-1 rounded px-1 py-0.5 text-left text-xs transition-colors',
            count > 0 ? 'text-blue-700 hover:bg-blue-50' : 'text-muted-foreground hover:bg-muted'
          )}
        >
          <MessageSquare className={cn('h-3.5 w-3.5 flex-shrink-0', count > 0 ? 'text-blue-600' : 'text-muted-foreground')} />
          <span className="w-4 flex-shrink-0 tabular-nums">{count || '-'}</span>
          {count > 0 && latestPreview ? (
            <span className="min-w-0 flex-1 truncate text-muted-foreground">{latestPreview}</span>
          ) : (
            <span className="min-w-0 flex-1 truncate text-muted-foreground">None</span>
          )}
        </button>
      </TooltipTrigger>
      <TooltipContent side="bottom" align="start" className="w-96 max-w-[calc(100vw-2rem)] p-0">
        <div className="max-h-80 overflow-y-auto p-3">
          <div className="mb-2 flex items-center justify-between gap-2">
            <span className="text-xs font-medium">Post Closing Comments</span>
            {count > 0 && (
              <span className="text-[11px] text-muted-foreground">{count} total</span>
            )}
          </div>
          {loading && !loaded ? (
            <div className="flex items-center gap-2 py-2 text-xs text-muted-foreground">
              <Loader2 className="h-3 w-3 animate-spin" />
              Loading comments...
            </div>
          ) : comments && comments.length > 0 ? (
            <div className="space-y-3">
              {comments.map((comment) => (
                <div key={comment.id} className="border-b border-border/60 pb-2 last:border-0 last:pb-0">
                  <div className="mb-1 flex flex-wrap items-center gap-x-2 gap-y-0.5">
                    <span className="text-xs font-medium">{comment.author_name || 'Unknown User'}</span>
                    <span className="text-[11px] text-muted-foreground">{formatAbsoluteDateTime(comment.created_at)}</span>
                  </div>
                  <p className="whitespace-pre-wrap break-words text-xs text-muted-foreground">
                    {comment.content}
                  </p>
                </div>
              ))}
            </div>
          ) : (
            <p className="py-2 text-xs text-muted-foreground">No Post Closing comments yet</p>
          )}
        </div>
      </TooltipContent>
    </Tooltip>
  );
}

// ── Row Component ──────────────────────────────────────────────────────────────

function WarehouseCells({ loan, onLoanUpdate }: Pick<PostClosingRowProps, 'loan' | 'onLoanUpdate'>) {
  const warehouse = useWarehouseLine(loan);
  const collateral = usePrimisCollateral(loan, true, updates => onLoanUpdate?.(loan.id, updates));
  return <>
    <TableCell className="w-[208px]"><WarehouseLineSelect control={warehouse} compact /></TableCell>
    <TableCell className="w-[152px]">
      {matchWarehouseLine(warehouse.value) === 'Primis Bank'
        ? <PrimisCollateralDate key={loan.id} control={collateral} compact />
        : <span className="text-muted-foreground" aria-label="Collateral request date not required">—</span>}
    </TableCell>
  </>;
}

function PostClosingRow({ loan, onViewDetails, onLoanUpdate, formatCurrency, formatDate, mersAuditMode, warehouseEnabled }: PostClosingRowProps) {
  const { user } = useAuth();

  // Local state for editable date fields
  const [readyToShipDate, setReadyToShipDate] = useState(loan.ready_to_ship_date || '');
  const [deliveryDate, setDeliveryDate] = useState(loan.shipping_investor_delivery_date || '');
  const [pccdDate, setPccdDate] = useState(toDateInputValue(loan.pccd_completed_at));
  const [mersDate, setMersDate] = useState(toDateInputValue(loan.mers_transferred_at));
  // Save states for each field
  const [readyToShipState, setReadyToShipState] = useState<DateSaveState>('idle');
  const [deliveryDateState, setDeliveryDateState] = useState<DateSaveState>('idle');
  const [pccdState, setPccdState] = useState<DateSaveState>('idle');
  const [mersState, setMersState] = useState<DateSaveState>('idle');

  // Copy loan number state
  const [copied, setCopied] = useState(false);

  // Final HUD upload state
  const [hudState, setHudState] = useState<DateSaveState>('idle');
  const hudInputRef = useRef<HTMLInputElement>(null);

  // Final HUD version history
  interface HudVersion {
    id: string;
    created_at: string;
    uploaded_by: string;
    source_file: string;
  }
  const [hudVersions, setHudVersions] = useState<HudVersion[]>([]);
  const [hudVersionsLoading, setHudVersionsLoading] = useState(false);

  const fetchHudVersions = useCallback(async () => {
    if (!loan.encompass_loan_id) {
return;
}
    setHudVersionsLoading(true);
    try {
      const { data: docs } = await supabase
        .from('loan_documents')
        .select('id, created_at, uploaded_by, source_file')
        .eq('encompass_loan_id', loan.encompass_loan_id)
        .eq('document_type', 'misc_settlement_statement')
        .filter('metadata->>is_final_hud', 'eq', 'true')
        .order('created_at', { ascending: false });

      if (!docs || docs.length === 0) {
        setHudVersions([]);
        return;
      }

      setHudVersions(docs);
    } catch (err) {
      log.error('Failed to fetch HUD versions', { loanId: loan.id }, err as Error);
    } finally {
      setHudVersionsLoading(false);
    }
  }, [loan.encompass_loan_id, loan.id]);

  const viewDoc = useCallback(async (storagePath: string) => {
    try {
      const url = await getDocumentDownloadUrl(storagePath);
      window.open(url, '_blank');
    } catch {
      toast.error('Failed to open document');
    }
  }, []);

  const getCurrentUserName = useCallback(() => {
    if (!user) {
return 'Unknown';
}
    return isExtendedUser(user)
      ? (user.fullName || user.email || 'Unknown')
      : (user.email || 'Unknown');
  }, [user]);

  // Sync local state when props change (e.g. after background refresh)
  useEffect(() => {
    if (readyToShipState === 'idle') {
setReadyToShipDate(loan.ready_to_ship_date || '');
}
  }, [loan.ready_to_ship_date, readyToShipState]);

  useEffect(() => {
    if (deliveryDateState === 'idle') {
setDeliveryDate(loan.shipping_investor_delivery_date || '');
}
  }, [loan.shipping_investor_delivery_date, deliveryDateState]);

  useEffect(() => {
    if (pccdState === 'idle') {
setPccdDate(toDateInputValue(loan.pccd_completed_at));
}
  }, [loan.pccd_completed_at, pccdState]);

  useEffect(() => {
    if (mersState === 'idle') {
setMersDate(toDateInputValue(loan.mers_transferred_at));
}
  }, [loan.mers_transferred_at, mersState]);

  // ── Ready to Ship — direct DB save ────────────────────────────────────────

  const handleReadyToShipChange = useCallback(async (value: string) => {
    setReadyToShipDate(value);
    if (!user?.id) {
return;
}

    try {
      setReadyToShipState('saving');

      const userName = getCurrentUserName();
      const updatedAt = new Date().toISOString();

      const { error } = await supabase.rpc('set_loan_ready_to_ship', {
        p_loan_id: loan.id,
        p_ready_to_ship_date: value || null
      });

      if (error) {
        log.error('Failed to save ready-to-ship date', { loanId: loan.id }, error as unknown as Error);
        toast.error('Failed to save ready-to-ship date');
        setReadyToShipDate(loan.ready_to_ship_date || '');
        setReadyToShipState('error');
        return;
      }

      toast.success('Ready to ship date saved');
      setReadyToShipState('synced');
      onLoanUpdate?.(loan.id, {
        ready_to_ship_date: value || undefined,
        ready_to_ship_set_by: user.id,
        ready_to_ship_set_by_name: userName,
        ready_to_ship_set_at: updatedAt
      });
      setTimeout(() => setReadyToShipState('idle'), 2000);
    } catch (err) {
      log.error('Error saving ready-to-ship date', { loanId: loan.id }, err as Error);
      toast.error('Failed to save ready-to-ship date');
      setReadyToShipDate(loan.ready_to_ship_date || '');
      setReadyToShipState('error');
      setTimeout(() => setReadyToShipState('idle'), 3000);
    }
  }, [user, loan.id, loan.ready_to_ship_date, onLoanUpdate, getCurrentUserName]);

  // ── Minor post-purchase tasks ─────────────────────────────────────────────

  const savePostClosingTaskDate = useCallback(async (task: PostClosingTask, value: string) => {
    const isPccd = task === 'pccd';
    const label = isPccd ? 'PCCD' : 'MERS';
    const setState = isPccd ? setPccdState : setMersState;
    const setDate = isPccd ? setPccdDate : setMersDate;
    const previousDate = isPccd
      ? toDateInputValue(loan.pccd_completed_at)
      : toDateInputValue(loan.mers_transferred_at);

    setDate(value);

    if (!user?.id || !loan.organization_id) {
return;
}
    if (task === 'mers' && value && !loan.first_pa_date) {
      setDate(previousDate);
      toast.error('MERS can be transferred after PA is issued');
      return;
    }

    const now = new Date().toISOString();
    const userName = getCurrentUserName();
    const completedAt = dateInputValueToTimestamp(value);
    const taskFields = isPccd
      ? {
          pccd_completed_at: completedAt,
          pccd_completed_by: completedAt ? user.id : null,
          pccd_completed_by_name: completedAt ? userName : null
        }
      : {
          mers_transferred_at: completedAt,
          mers_transferred_by: completedAt ? user.id : null,
          mers_transferred_by_name: completedAt ? userName : null
        };

    try {
      setState('saving');

      const { error } = await supabase
        .from('loan_post_closing_tracking')
        .upsert({
          organization_id: loan.organization_id,
          encompass_loan_id: loan.encompass_loan_id,
          ...taskFields,
          updated_by: user.id,
          updated_by_name: userName,
          updated_at: now
        }, { onConflict: 'organization_id,encompass_loan_id' });

      if (error) {
        log.error('Failed to save post-closing task', { loanId: loan.id, task }, error as unknown as Error);
        toast.error(`Failed to save ${label}`);
        setDate(previousDate);
        setState('error');
        setTimeout(() => setState('idle'), 3000);
        return;
      }

      onLoanUpdate?.(loan.id, isPccd
        ? {
            pccd_completed_at: completedAt || undefined,
            pccd_completed_by: completedAt ? user.id : undefined,
            pccd_completed_by_name: completedAt ? userName : undefined
          }
        : {
            mers_transferred_at: completedAt || undefined,
            mers_transferred_by: completedAt ? user.id : undefined,
            mers_transferred_by_name: completedAt ? userName : undefined
          });

    } catch (err) {
      log.error('Error saving post-closing task', { loanId: loan.id, task }, err as Error);
      toast.error(`Failed to save ${label}`);
      setDate(previousDate);
      setState('error');
      setTimeout(() => setState('idle'), 3000);
      return;
    }

    // PCCD has no Encompass field — the local save is the whole operation.
    if (task !== 'mers') {
      toast.success(`${label} ${completedAt ? 'date saved' : 'cleared'}`);
      setState('synced');
      setTimeout(() => setState('idle'), 2000);
      return;
    }

    // ── Phase B: push MERS date to Encompass CX.MERSTRANSFERDATE (best-effort).
    // The local save above is the source of truth — a failure here must NOT
    // revert it; it only surfaces as a sync error/pending indicator. Mirrors
    // handleDeliveryDateChange. Sends '' on a clear, which the edge fn accepts
    // via allow_blank_clear and pushes a blank to Encompass.
    try {
      setState('saving');

      const { data, error } = await supabase.functions.invoke('enqueue-encompass-write', {
        body: {
          loanId: loan.id,
          encompassLoanId: loan.encompass_loan_id,
          fieldId: 'CX.MERSTRANSFERDATE',
          value: value || ''
        }
      });

      if (error || !data?.success) {
        log.error('Failed to sync MERS date to Encompass', { loanId: loan.id }, error as Error);
        toast.error(`MERS saved locally — Encompass sync failed${data?.error ? `: ${data.error}` : ''}`);
        setState('error');
        setTimeout(() => setState('idle'), 4000);
        return;
      }

      if (data.status === 'pending') {
        toast.info(`MERS saved. ${loan.file_locked_by_name || 'Someone'} has this loan open - will sync when they close it.`);
        setState('pending');
      } else {
        toast.success(`MERS ${completedAt ? 'date synced' : 'cleared'} to Encompass`);
        setState('synced');
      }
      setTimeout(() => setState('idle'), 3000);
    } catch (err) {
      log.error('Error syncing MERS date to Encompass', { loanId: loan.id }, err as Error);
      toast.error('MERS saved locally — Encompass sync failed');
      setState('error');
      setTimeout(() => setState('idle'), 4000);
    }
  }, [
    user,
    loan.id,
    loan.organization_id,
    loan.encompass_loan_id,
    loan.first_pa_date,
    loan.pccd_completed_at,
    loan.mers_transferred_at,
    loan.file_locked_by_name,
    onLoanUpdate,
    getCurrentUserName
  ]);

  // ── Delivered to Investor (Field 2012) — Encompass sync ──────────────────

  const handleDeliveryDateChange = useCallback(async (value: string) => {
    setDeliveryDate(value);

    try {
      setDeliveryDateState('saving');

      const { data, error } = await supabase.functions.invoke('enqueue-encompass-write', {
        body: {
          loanId: loan.id,
          encompassLoanId: loan.encompass_loan_id,
          fieldId: '2012',
          value: value || ''
        }
      });

      if (error || !data?.success) {
        log.error('Failed to save delivery date', { loanId: loan.id }, error as Error);
        toast.error(data?.error || 'Failed to save delivery date');
        setDeliveryDate(loan.shipping_investor_delivery_date || '');
        setDeliveryDateState('error');
        setTimeout(() => setDeliveryDateState('idle'), 3000);
        return;
      }

      onLoanUpdate?.(loan.id, { shipping_investor_delivery_date: value || undefined });
      if (data.status === 'pending') {
        toast.info(`Saved. ${loan.file_locked_by_name || 'Someone'} has this loan open - will sync when they close it.`);
        setDeliveryDateState('pending');
      } else {
        toast.success('Delivery date synced to Encompass');
        setDeliveryDateState('synced');
      }
      setTimeout(() => setDeliveryDateState('idle'), 3000);
    } catch (err) {
      log.error('Error saving delivery date', { loanId: loan.id }, err as Error);
      toast.error('Failed to save delivery date');
      setDeliveryDate(loan.shipping_investor_delivery_date || '');
      setDeliveryDateState('error');
      setTimeout(() => setDeliveryDateState('idle'), 3000);
    }
  }, [loan.id, loan.encompass_loan_id, loan.shipping_investor_delivery_date, loan.file_locked_by_name, onLoanUpdate]);


  // ── Final HUD upload ───────────────────────────────────────────────────

  const handleFinalHudUpload = useCallback(async (file: File) => {
    if (!user?.id) {
return;
}

    try {
      setHudState('saving');

      const formData = new FormData();
      formData.append('file', file);
      formData.append('loanId', loan.id);
      formData.append('encompassLoanId', loan.encompass_loan_id);

      const { data, error } = await supabase.functions.invoke('upload-final-hud', {
        body: formData
      });

      if (error || !data?.success) {
        log.error('Failed to upload Final HUD', { loanId: loan.id }, error as Error);
        toast.error(data?.error || 'Failed to upload Final HUD');
        setHudState('error');
        setTimeout(() => setHudState('idle'), 3000);
        return;
      }

      toast.success('Final HUD uploaded');
      onLoanUpdate?.(loan.id, {
        final_hud_received_date: data.date,
        final_hud_storage_path: data.storagePath,
        final_hud_uploaded_by: user.id,
        final_hud_sync_status: 'pending'
      });
      setHudState('synced');
      fetchHudVersions(); // Refresh version list
      setTimeout(() => setHudState('idle'), 2000);
    } catch (err) {
      log.error('Error uploading Final HUD', { loanId: loan.id }, err as Error);
      toast.error('Failed to upload Final HUD');
      setHudState('error');
      setTimeout(() => setHudState('idle'), 3000);
    }
  }, [user, loan.id, loan.encompass_loan_id, onLoanUpdate, fetchHudVersions]);


  // ── Computed fields ───────────────────────────────────────────────────────

  // Lock met — delivery date <= lock expiration
  const lockExpires = loan.rate_lock_buy_side_lock_expires;
  const lockMet = deliveryDate && lockExpires
    ? parseAsLocalDate(deliveryDate) <= parseAsLocalDate(lockExpires)
    : null;

  // Days since funded
  const daysSinceFunded = loan.funded_date
    ? differenceInDays(new Date(), parseAsLocalDate(loan.funded_date))
    : null;

  // ── Sync status indicator ─────────────────────────────────────────────────

  const SyncIndicator = ({ state }: { state: DateSaveState }) => {
    if (state === 'saving') {
return <Loader2 className="h-3 w-3 animate-spin text-muted-foreground" />;
}
    if (state === 'synced') {
return <Check className="h-3 w-3 text-green-600" />;
}
    if (state === 'pending') {
return <Clock className="h-3 w-3 text-orange-500" title="Queued — will sync when file is closed" />;
}
    if (state === 'error') {
return <XCircle className="h-3 w-3 text-red-500" />;
}
    return null;
  };

  const handleCopyLoanNumber = useCallback(async (e: React.MouseEvent) => {
    e.stopPropagation();
    const num = loan.trans_details_loan_number;
    if (!num) {
return;
}
    try {
      await navigator.clipboard.writeText(num);
      setCopied(true);
      toast.success(`Copied ${num}`);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error('Failed to copy to clipboard');
    }
  }, [loan.trans_details_loan_number]);

  const handleRowClick = useCallback((event: React.MouseEvent<HTMLTableRowElement>) => {
    const target = event.target as HTMLElement | null;
    if (target?.closest(
      'button, a, input, select, textarea, [role="button"], [role="menuitem"], [data-radix-collection-item], [data-row-interactive="true"]'
    )) {
      return;
    }
    onViewDetails(loan);
  }, [loan, onViewDetails]);

  const TaskCell = ({ task }: { task: PostClosingTask }) => {
    const isPccd = task === 'pccd';
    const label = isPccd ? 'PCCD' : 'MERS';
    const completedBy = isPccd ? loan.pccd_completed_by_name : loan.mers_transferred_by_name;
    const state = isPccd ? pccdState : mersState;
    const value = isPccd ? pccdDate : mersDate;
    const canEdit = isPccd || !!loan.first_pa_date || !!value;
    const disabled = isSavingState(state) || !canEdit;

    const tooltip = value
      ? `${label} date${completedBy ? ` set by ${completedBy}` : ''}`
      : canEdit
      ? `Set ${label} date`
      : 'MERS can be transferred after PA is issued';

    return (
      <Tooltip>
        <TooltipTrigger asChild>
          <span className="inline-flex items-center gap-1" data-row-interactive="true">
            <input
              type="date"
              aria-label={`${label} date`}
              value={value}
              disabled={disabled}
              onChange={(e) => savePostClosingTaskDate(task, e.target.value)}
              className={cn(
                'h-7 w-[96px] rounded border bg-background px-1 text-xs tabular-nums transition-colors',
                'focus:outline-none focus:ring-1 focus:ring-ring',
                value
                  ? 'border-green-200 bg-green-50 text-green-700 hover:bg-green-100'
                  : 'border-gray-200 bg-background text-muted-foreground hover:bg-muted',
                isSavingState(state) && 'cursor-wait opacity-70',
                state === 'error' && 'border-red-300 text-red-600',
                disabled && 'cursor-not-allowed opacity-50 hover:bg-background'
              )}
            />
            <SyncIndicator state={state} />
          </span>
        </TooltipTrigger>
        <TooltipContent>{tooltip}</TooltipContent>
      </Tooltip>
    );
  };

  return (
    <TooltipProvider>
    <TableRow
      className="cursor-pointer hover:bg-muted/50"
      onClick={handleRowClick}
    >
      {/* Loan # */}
      <TableCell className="w-[92px] flex-shrink-0 font-medium">
        {loan.trans_details_loan_number ? (
          <Tooltip>
            <TooltipTrigger asChild>
              <button
                onClick={handleCopyLoanNumber}
                className="truncate hover:text-primary hover:underline cursor-pointer flex items-center gap-1 group"
              >
                {loan.trans_details_loan_number}
                {copied ? (
                  <Check className="h-3 w-3 text-green-500 flex-shrink-0" />
                ) : (
                  <Copy className="h-3 w-3 opacity-0 group-hover:opacity-50 transition-opacity flex-shrink-0" />
                )}
              </button>
            </TooltipTrigger>
            <TooltipContent>Click to copy loan number</TooltipContent>
          </Tooltip>
        ) : (
          <span className="truncate">-</span>
        )}
      </TableCell>

      {/* Borrower */}
      <TableCell className="w-[126px] flex-shrink-0 truncate">
        {loan.borrower_first_name} {loan.borrower_last_name}
      </TableCell>

      {/* Amount */}
      <TableCell className="w-[96px] flex-shrink-0">
        {formatCurrency(loan.trans_details_loan_amt)}
      </TableCell>

      {/* Investor */}
      <TableCell className="w-[118px] flex-shrink-0 truncate" title={loan.investor_name || undefined}>
        {loan.investor_name || '-'}
      </TableCell>

      {/* Warehouse and portal-only collateral request */}
      {warehouseEnabled && !mersAuditMode && (
        <WarehouseCells loan={loan} onLoanUpdate={onLoanUpdate} />
      )}
      <TableCell className="w-[76px] flex-shrink-0 text-sm text-gray-500">
        {formatDate(loan.funded_date)}
      </TableCell>

      {/* Purchased — MERS audit only */}
      {mersAuditMode && (
        <TableCell className="w-[76px] flex-shrink-0 text-sm text-gray-500">
          {formatDate(loan.purchased_date)}
        </TableCell>
      )}

      {/* Final HUD */}
      <TableCell className="w-[88px] flex-shrink-0">
        {hudState === 'saving' ? (
          <span className="flex items-center gap-1 text-xs text-muted-foreground">
            <Loader2 className="h-3 w-3 animate-spin" />
            Uploading...
          </span>
        ) : loan.final_hud_received_date ? (
          <Popover onOpenChange={(open) => {
 if (open) {
fetchHudVersions();
} 
}}>
            <PopoverTrigger asChild>
              <button className="text-sm text-blue-600 hover:text-blue-800 hover:underline text-left">
                {formatDate(loan.final_hud_received_date)}
              </button>
            </PopoverTrigger>
            <PopoverContent className="w-80 p-3" align="start">
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium">Final HUD Versions</span>
                  <button
                    onClick={() => hudInputRef.current?.click()}
                    className="text-xs text-blue-600 hover:text-blue-800 font-medium"
                  >
                    + Upload New
                  </button>
                </div>
                <Separator />
                {hudVersionsLoading ? (
                  <div className="flex items-center justify-center py-3">
                    <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
                  </div>
                ) : hudVersions.length === 0 ? (
                  <p className="text-xs text-muted-foreground py-2">No versions found</p>
                ) : (
                  <div className="space-y-1 max-h-48 overflow-y-auto">
                    {hudVersions.map(v => (
                      <div key={v.id} className="flex items-center justify-between text-xs py-1.5 px-1 rounded hover:bg-muted/50">
                        <div className="flex items-center gap-2 min-w-0">
                          <FileText className="h-3.5 w-3.5 text-muted-foreground flex-shrink-0" />
                          <span className="font-medium">{formatDate(v.created_at)}</span>
                          <span className="text-muted-foreground truncate">{v.uploaded_by}</span>
                        </div>
                        <div className="flex items-center gap-2 flex-shrink-0">
                          <button
                            onClick={() => viewDoc(v.source_file)}
                            className="text-blue-600 hover:text-blue-800 font-medium"
                          >
                            View
                          </button>
                          {/* Derive sync status at render time from loan props — not cached in hudVersions */}
                          {v.source_file === loan.final_hud_storage_path ? (
                            loan.final_hud_sync_status === 'synced' ? (
                              <Check className="h-3 w-3 text-green-600" />
                            ) : loan.final_hud_sync_status === 'failed' ? (
                              <XCircle className="h-3 w-3 text-red-500" />
                            ) : (
                              <Clock className="h-3 w-3 text-orange-500" />
                            )
                          ) : null}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </PopoverContent>
          </Popover>
        ) : (
          <button
            onClick={() => hudInputRef.current?.click()}
            className="inline-flex items-center gap-1 text-xs px-2 py-1 border border-blue-700 rounded bg-blue-50 text-blue-700 hover:bg-blue-100 hover:border-blue-800 transition-colors disabled:opacity-50"
          >
            <Upload className="h-3 w-3" />
            Upload
          </button>
        )}
        <input
          ref={hudInputRef}
          type="file"
          accept="application/pdf"
          className="hidden"
          onChange={(e) => {
            const file = e.target.files?.[0];
            if (file) {
handleFinalHudUpload(file);
}
            e.target.value = '';
          }}
        />
      </TableCell>

      {/* Comments */}
      <TableCell className="w-[126px] flex-shrink-0">
        <PostClosingCommentsCell loan={loan} />
      </TableCell>

      {/* Ready to Ship — editable */}
      <TableCell className="w-[118px] flex-shrink-0">
        <div className="flex items-center gap-1">
          <input
            type="date"
            value={readyToShipDate}
            onChange={(e) => handleReadyToShipChange(e.target.value)}
            disabled={readyToShipState === 'saving'}
            className={cn(
              'w-[96px] h-7 px-1.5 text-xs border rounded bg-background',
              'focus:outline-none focus:ring-1 focus:ring-ring',
              'disabled:opacity-50 disabled:cursor-not-allowed',
              readyToShipState === 'error' && 'border-red-400'
            )}
          />
          <SyncIndicator state={readyToShipState} />
        </div>
      </TableCell>

      {/* Delivered to Investor — editable when file not locked, Encompass sync */}
      <TableCell className="w-[118px] flex-shrink-0">
        <div className="flex items-center gap-1">
          <input
            type="date"
            value={deliveryDate}
            onChange={(e) => handleDeliveryDateChange(e.target.value)}
            disabled={deliveryDateState === 'saving' || !!loan.is_file_locked}
            className={cn(
              'w-[96px] h-7 px-1.5 text-xs border rounded bg-background',
              'focus:outline-none focus:ring-1 focus:ring-ring',
              'disabled:opacity-50 disabled:cursor-not-allowed',
              deliveryDateState === 'error' && 'border-red-400'
            )}
          />
          {loan.is_file_locked ? (
            <Tooltip>
              <TooltipTrigger asChild>
                <span className="inline-flex cursor-help" data-row-interactive="true">
                  <Lock className="h-4 w-4 text-orange-500" />
                </span>
              </TooltipTrigger>
              <TooltipContent>Locked by {loan.file_locked_by_name || 'someone'}</TooltipContent>
            </Tooltip>
          ) : (
            <SyncIndicator state={deliveryDateState} />
          )}
        </div>
      </TableCell>

      {/* PCCD */}
      <TableCell className="w-[112px] flex-shrink-0 text-center">
        <TaskCell task="pccd" />
      </TableCell>

      {/* MERS */}
      <TableCell className="w-[112px] flex-shrink-0 text-center">
        <TaskCell task="mers" />
      </TableCell>

      {/* Lock Exp */}
      <TableCell className="w-[76px] flex-shrink-0 text-sm text-gray-500">
        {formatDate(lockExpires)}
      </TableCell>

      {/* Timing */}
      <TableCell className="w-[70px] flex-shrink-0">
        <div className="flex items-center justify-center gap-2">
          {lockMet === true ? (
            <CheckCircle2 className="h-4 w-4 text-green-600" />
          ) : lockMet === false ? (
            <XCircle className="h-4 w-4 text-red-500" />
          ) : (
            <span className="text-gray-400 text-sm">-</span>
          )}
          {daysSinceFunded !== null ? (
            <span className={cn(
              'text-sm font-medium',
              daysSinceFunded <= 7 ? 'text-green-600' :
              daysSinceFunded <= 14 ? 'text-yellow-600' :
              daysSinceFunded <= 21 ? 'text-orange-600' :
              'text-red-600'
            )}>
              {daysSinceFunded}
            </span>
          ) : (
            <span className="text-gray-400 text-sm">-</span>
          )}
        </div>
      </TableCell>

      {/* Progress Stepper — hidden in MERS audit (irrelevant to a MERS gap audit) */}
      {!mersAuditMode && (
        <TableCell className="w-[88px] flex-shrink-0">
          <PostClosingProgress loan={loan} variant="compact" />
        </TableCell>
      )}

      {/* Actions */}
      <TableCell className="w-[50px] flex-shrink-0">
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
              <span className="sr-only">Open menu</span>
              <MoreVertical className="h-4 w-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem onClick={(e) => {
              e.stopPropagation();
              onViewDetails(loan);
            }}>
              <Eye className="mr-2 h-4 w-4" />
              View Details
            </DropdownMenuItem>
            <DropdownMenuItem onClick={(e) => {
              e.stopPropagation();
              window.open(`/internal/documents/${loan.encompass_loan_id}`, '_blank', 'noopener');
            }}>
              <FileText className="mr-2 h-4 w-4" />
              Documents
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </TableCell>
    </TableRow>
    </TooltipProvider>
  );
}

// ── Main Table Component ───────────────────────────────────────────────────────

export function PostClosingTable({
  prospects,
  isLoading,
  isLoadingMore = false,
  hasMore = false,
  totalCount = 0,
  sortConfig,
  onSort,
  onViewDetails,
  onLoadMore,
  onLoanUpdate,
  mersAuditMode = false
}: PostClosingTableProps) {
  const { enabled: warehouseEnabled } = useFeatureFlag('warehouse_line_v1');
  const parentRef = useRef<HTMLDivElement>(null);
  const loadMoreRef = useRef<HTMLDivElement>(null);

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount);
  };

  const formatDate = (dateString: string | null | undefined) => {
    if (!dateString) {
return '-';
}
    try {
      const date = new Date(dateString.includes('T') ? dateString : `${dateString}T00:00:00`);
      if (isNaN(date.getTime()) || date.getTime() === 0) {
return '-';
}
      return format(date, 'M/d/yy');
    } catch {
      return '-';
    }
  };

  const SortIcon = ({ field }: { field: string }) => {
    if (sortConfig.field !== field) {
      return <ArrowUpDown className="h-3 w-3 text-muted-foreground/50 flex-shrink-0" />;
    }
    return sortConfig.direction === 'asc' ? (
      <ArrowUp className="h-3 w-3 flex-shrink-0" />
    ) : (
      <ArrowDown className="h-3 w-3 flex-shrink-0" />
    );
  };

  const SortableHeader = ({ field, children }: { field: string; children: React.ReactNode }) => (
    <button
      onClick={() => onSort(field)}
      className="flex items-center gap-1 whitespace-nowrap text-left text-xs font-medium hover:text-foreground transition-colors"
    >
      {children}
      <SortIcon field={field} />
    </button>
  );

  // Infinite scroll observer
  useEffect(() => {
    if (!loadMoreRef.current || !onLoadMore || !hasMore || isLoadingMore) {
return;
}

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasMore && !isLoadingMore) {
          onLoadMore();
        }
      },
      {
        threshold: 0.1,
        root: parentRef.current
      }
    );

    observer.observe(loadMoreRef.current);
    return () => observer.disconnect();
  }, [onLoadMore, hasMore, isLoadingMore]);

  // Render rows using stable callback
  const renderRow = useCallback((prospect: ProspectLoan) => (
    <PostClosingRow
      key={prospect.id}
      loan={prospect}
      onViewDetails={onViewDetails}
      onLoanUpdate={onLoanUpdate}
      formatCurrency={formatCurrency}
      formatDate={formatDate}
      mersAuditMode={mersAuditMode}
      warehouseEnabled={warehouseEnabled}
    />
  ), [onViewDetails, onLoanUpdate, mersAuditMode, warehouseEnabled]);

  // Only show skeleton on initial load when we have no data
  if (isLoading && prospects.length === 0) {
    return (
      <div className="space-y-2">
        {[1, 2, 3, 4, 5].map((i) => (
          <div key={i} className="h-16 bg-gray-100 animate-pulse rounded" />
        ))}
      </div>
    );
  }

  if (prospects.length === 0) {
    return (
      <div className="text-center py-12 border rounded-lg">
        <p className="text-gray-500 text-lg">No post-closing loans found</p>
        <p className="text-gray-400 text-sm mt-2">
          Loans appear here after they are funded
        </p>
      </div>
    );
  }

  return (
    <div className="min-w-0 max-w-full border rounded-lg flex flex-col overflow-hidden">
      <div ref={parentRef} data-post-closing-scroll role="region" aria-label="Scrollable Post Closing table" tabIndex={0}
        className={cn('overflow-auto focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring',
          isLoading && prospects.length > 0 && 'opacity-50 pointer-events-none transition-opacity')}
        style={{ maxHeight: 'calc(100vh - 400px)', minHeight: '400px' }}>
        <Table className="table-fixed" containerProps={{ className: 'overflow-visible' }}>
          <TableHeader className="[&_th]:sticky [&_th]:top-0 [&_th]:z-10 [&_th]:bg-background">
            <TableRow>
              <TableHead className="w-[92px] flex-shrink-0">
                <SortableHeader field="trans_details_loan_number">Loan #</SortableHeader>
              </TableHead>
              <TableHead className="w-[126px] flex-shrink-0">
                <SortableHeader field="borrower_last_name">Borrower</SortableHeader>
              </TableHead>
              <TableHead className="w-[96px] flex-shrink-0">
                <SortableHeader field="trans_details_loan_amt">Amount</SortableHeader>
              </TableHead>
              <TableHead className="w-[118px] flex-shrink-0">
                <SortableHeader field="investor_name">Investor</SortableHeader>
              </TableHead>
              {warehouseEnabled && !mersAuditMode && (
                <>
                  <TableHead scope="col" className="w-[208px] text-xs">Warehouse</TableHead>
                  <TableHead scope="col" className="w-[152px] text-xs">Collateral requested</TableHead>
                </>
              )}
              <TableHead className="w-[76px] flex-shrink-0">
                <SortableHeader field="funded_date">Funded</SortableHeader>
              </TableHead>
              {mersAuditMode && (
                <TableHead className="w-[76px] flex-shrink-0">
                  <SortableHeader field="purchased_date">Purchased</SortableHeader>
                </TableHead>
              )}
              <TableHead className="w-[88px] flex-shrink-0">
                <SortableHeader field="final_hud_received_date">Final HUD</SortableHeader>
              </TableHead>
              <TableHead className="w-[126px] flex-shrink-0">
                <span className="text-xs font-medium">Comments</span>
              </TableHead>
              <TableHead className="w-[118px] flex-shrink-0">
                <SortableHeader field="ready_to_ship_date">Ready to Ship</SortableHeader>
              </TableHead>
              <TableHead className="w-[118px] flex-shrink-0">
                <SortableHeader field="shipping_investor_delivery_date">Delivered</SortableHeader>
              </TableHead>
              <TableHead className="w-[112px] flex-shrink-0 text-center">
                <span className="text-xs font-medium">PCCD</span>
              </TableHead>
              <TableHead className="w-[112px] flex-shrink-0 text-center">
                <SortableHeader field="mers_transferred_at">MERS</SortableHeader>
              </TableHead>
              <TableHead className="w-[76px] flex-shrink-0">
                <SortableHeader field="rate_lock_buy_side_lock_expires">Lock Exp</SortableHeader>
              </TableHead>
              <TableHead className="w-[70px] flex-shrink-0 text-center">
                <span className="text-xs font-medium">Timing</span>
              </TableHead>
              {!mersAuditMode && (
                <TableHead className="w-[88px] flex-shrink-0">
                  <span className="text-xs font-medium">Progress</span>
                </TableHead>
              )}
              <TableHead className="w-[50px] flex-shrink-0">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {prospects.map((prospect) => renderRow(prospect))}
          </TableBody>
        </Table>

        {/* Infinite scroll sentinel */}
        {hasMore && (
          <div
            ref={loadMoreRef}
            className="flex justify-center py-4"
          >
            {isLoadingMore && (
              <div className="flex items-center gap-2 text-gray-500">
                <Loader2 className="h-4 w-4 animate-spin" />
                <span>Loading more...</span>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Footer with count */}
      <div className="border-t px-4 py-2 text-sm text-gray-500 flex items-center justify-between">
        <span>
          Showing {prospects.length.toLocaleString()} of {totalCount.toLocaleString()} loans
        </span>
        {hasMore && !isLoadingMore && (
          <Button
            variant="ghost"
            size="sm"
            onClick={onLoadMore}
            className="text-blue-600 hover:text-blue-700"
          >
            Load more
          </Button>
        )}
      </div>
    </div>
  );
}
